import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Reveals its `[data-reveal]` descendants once, when the element first scrolls into view.
 *
 * Content stays visible without JavaScript: the hidden state in
 * `snippets/scroll-reveal-styles.liquid` only applies once this element is `:defined`.
 * The `revealed` attribute is the single source of truth for the CSS.
 *
 * @extends Component
 */
export class ScrollReveal extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #revealed = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#revealed || this.hasAttribute('revealed')) {
      this.reveal();
      return;
    }

    if (prefersReducedMotion()) {
      this.reveal();
      return;
    }

    // Trigger once the top edge passes 85% of the viewport height, so the motion is seen
    // rather than finished below the fold.
    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      rootMargin: '0px 0px -15% 0px',
    });
    this.#observer.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();

    // A section re-render morphs attributes back to the server markup, which has no `revealed`.
    if (this.#revealed) this.setAttribute('revealed', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * Shows the content immediately. Safe to call more than once.
   */
  reveal() {
    this.#revealed = true;
    this.setAttribute('revealed', '');
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) this.reveal();
  };
}

if (!customElements.get('scroll-reveal')) {
  customElements.define('scroll-reveal', ScrollReveal);
}
